import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { usePlacesWidget } from "react-google-autocomplete";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { healthOrganisationSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import {
  useUpdateHealthOrganisationDetailsMutation,
  useGetHealthOrganisationDetailsQuery,
} from "../../services/api";
import style from "./heathOrganisation.module.css";
import { Button, LabelBox } from "../../components";
import { appConfig } from "../../hooks/useConfig";
import { colors } from "../../theme/colors";

type FormState = typeof healthOrganisationSchema.__outputType;

const EditHealthOrganisation = () => {
  const [updateHealthOrganisation] = useUpdateHealthOrganisationDetailsMutation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [faxNumberSelected, setFaxNumberSelected] = useState(false);
  const [emailSelected, setEmailSelected] = useState(false);
  const [error, setError] = useState(false);
  const [deliveryModeError, setDeliveryModeError] = useState(false);
  const [isSavingHealthOrg, setIsSavingHealthOrg] = useState(false);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);

  // Get Health Organisation Details mutation hook
  const {
    data: healthOrganisationDetails,
    isLoading,
    isFetching,
  } = useGetHealthOrganisationDetailsQuery(id ?? "");

  const [message, setMessage] = useState<LabelMessageType | null>(null);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(healthOrganisationSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(healthOrganisationSchema),
    mode: "onChange",
  });

  const { ref: materialRef } = usePlacesWidget({
    apiKey: appConfig?.GOOGLE_API_KEY,
    inputAutocompleteValue: "off",
    options: {
      types: [
        "street_address",
        "street_number",
        "intersection",
        "route",
        "health",
      ],
      componentRestrictions: { country: "au" },
    },
    onPlaceSelected: (place) => {
      let suburb = "";
      let state = "";
      let postal = "";
      let streetName = '';
      let streetNumber = '';
      if (place) {
        clearErrors("postcode");
        clearErrors("suburb");
        clearErrors("state");
        clearErrors('street_number')
        clearErrors('street_name');
        const address_comp = place?.address_components;
        if (address_comp) {
          for (const c of address_comp) {
            const types: Array<string> = c.types;
            if (
              !suburb &&
              (types.includes("locality") ||
                types.includes("sublocality") ||
                types.includes("sublocality_level_1") ||
                types.includes("postal_town") ||
                types.includes("administrative_area_level_3"))
            ) {
              suburb = c.short_name;
            }

            if (types.includes("administrative_area_level_1")) {
              state = c.short_name;
            } else if (types.includes("postal_code")) {
              postal = c.short_name;
            }
            if (types.includes('street_number')) {
              streetNumber = c.short_name;
            }
            if (types.includes('route')) {
              streetName = c.short_name;
            }
          }
          if (streetName === '' || streetNumber === '') {
            const match = place?.formatted_address?.match(/^(\d+)\s+(.+?),/);
            if (match) {
              streetNumber = match[1];
              streetName = match[2];
            } else {
              const address_arr = place?.formatted_address?.split(', ') ?? [];
              if (address_arr?.length > 3) {
                streetName = address_arr?.slice(0, 2).join(', ');
              } else {
                streetName = address_arr?.[0];
              }
            }
          }
          setValue("state", state);
          setValue("suburb", suburb);
          setValue("postcode", postal);
          setValue('street_number', streetNumber)
          setValue('street_name', streetName);
          const lat: any = get(place, "geometry.location.lat", () => { });
          const lng: any = get(place, "geometry.location.lng", () => { });
          setLatitude(lat() ?? 0);
          setLongitude(lng() ?? 0);
        }
      }
    },
  });

  useEffect(() => {
    if (!isEmpty(healthOrganisationDetails)) {
      setFaxNumberSelected(healthOrganisationDetails?.address?.is_fax_delivery ?? false);
      setEmailSelected(healthOrganisationDetails?.address?.is_mail_delivery ?? false);
      reset({
        name: healthOrganisationDetails?.name,
        provider_number: healthOrganisationDetails?.address?.provider_number,
        phone:
          healthOrganisationDetails?.address?.phone,
        account_manager_mail:
          healthOrganisationDetails?.account_manager_mail,
        fax: healthOrganisationDetails?.address?.fax ?? "",
        mail: healthOrganisationDetails?.address?.mail ?? "",
        postcode: healthOrganisationDetails?.address?.postcode,
        state: healthOrganisationDetails?.address?.state,
        suburb: healthOrganisationDetails?.address?.suburb,
        street_number: healthOrganisationDetails?.address?.street_number,
        street_name: healthOrganisationDetails?.address?.street_name,
        unit_number: healthOrganisationDetails?.address?.unit_number ?? "",
      });
      setLatitude(healthOrganisationDetails?.address?.latitude ?? 0);
      setLongitude(healthOrganisationDetails?.address?.longitude ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthOrganisationDetails]);

  const onSubmit = async (values: FormState) => {
    try {
      const {
        fax,
        mail,
        is_fax_delivery,
        is_mail_delivery,
        phone,
        ...rest
      } = values;
      if (mail === "" && fax === "") {
        setDeliveryModeError(true);
        return;
      }
      if (!faxNumberSelected && !emailSelected) {
        setError(true);
        return;
      }
      setIsSavingHealthOrg(true);
      const payload: EditHealthOrganisationPayload = {
        ...rest,
        id: parseInt(id ?? "0"),
        phone:
          phone?.replace(/ /g, ""),
        fax: fax?.replace(/ /g, ""),
        mail: mail ?? "",
        is_fax_delivery: is_fax_delivery ?? false,
        is_mail_delivery: is_mail_delivery ?? false,
        latitude,
        longitude,
      };
      await updateHealthOrganisation(payload).unwrap();
      setIsSavingHealthOrg(false);
      navigate(-1);
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.data?.detail?.toString() || "Please enter valid details",
      });
      setIsSavingHealthOrg(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading2}
            sx={{ marginTop: "10px" }}
          >
            Health Organisation Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="Enter name"
                label="Health Organisation Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
              <Input
                placeholder="Enter health organisation provider name"
                label="Health Organisation Provider Number"
                required
                register={register}
                name="provider_number"
                errors={errors}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Enter unit number"
                label="Unit Number"
                register={register}
                name="unit_number"
                errors={errors}
              />
              <Input
                placeholder="Enter street number"
                label="Street Number"
                required
                register={register}
                name="street_number"
                errors={errors}
                inputRef={materialRef}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Enter street name"
                label="Street name"
                required
                register={register}
                name="street_name"
                errors={errors}
              />
              <Input
                placeholder="Enter suburb"
                label="Suburb"
                required
                register={register}
                name="suburb"
                errors={errors}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Select state"
                label="State"
                required
                register={register}
                name="state"
                errors={errors}
              />
              <Input
                placeholder="Enter postcode"
                label="Postcode"
                required
                register={register}
                name="postcode"
                errors={errors}
              />
            </Box>
            <Typography className={style.detailHeading}>
              Contact Details
            </Typography>
            <Box className={style.flexBottom}>
              <Box className={style.row}>
                <Input
                  placeholder="Enter phone number"
                  label="Phone Number"
                  required
                  register={register}
                  name="phone"
                  errors={errors}
                />
                <Input
                  placeholder="Enter accounts manager email address"
                  label="Email Address (Accounts Manager)"
                  required
                  register={register}
                  name="account_manager_mail"
                  errors={errors}
                />
              </Box>
              <Typography className={style.detailHeading}>
                Request Form Delivery Details (Medical Records)
              </Typography>
              <Box className={style.row}>
                <Input
                  placeholder="Enter email address"
                  label="Email"
                  register={register}
                  name="mail"
                  errors={errors}
                  onFocus={() => {
                    setDeliveryModeError(false);
                  }}
                  onChange={(event) => {
                    setValue("mail", event.target.value);
                    if (event.target.value === "") {
                      setEmailSelected(false);
                    }
                  }}
                />
                <Input
                  placeholder="Enter fax number"
                  label="Fax Number"
                  register={register}
                  name="fax"
                  errors={errors}
                  onFocus={() => {
                    setDeliveryModeError(false);
                  }}
                  onChange={(event) => {
                    setValue("fax", event.target.value, {
                      shouldValidate: true,
                    });
                    clearErrors("fax");
                    if (event.target.value === "") {
                      setFaxNumberSelected(false);
                    }
                  }}
                />
              </Box>
              {deliveryModeError && (
                <Typography sx={{ color: colors.red, fontSize: "12px" }}>
                  * Please provide either Email or Fax Number.
                </Typography>
              )}
              <Typography className={style.detailHeading}>
                Mode of Delivery
              </Typography>
              <Box sx={{ marginLeft: 1 }}>
                <FormControlLabel
                  sx={{ "& span": { py: 0, px: 0.3 } }}
                  control={
                    <Checkbox
                      {...register("is_mail_delivery")}
                      sx={{ color: error ? colors.red : colors.black }}
                      onChange={() => {
                        setError(false);
                        setEmailSelected(!emailSelected);
                      }}
                      checked={emailSelected}
                    />
                  }
                  label="Email"
                  disabled={!watch("mail")}
                />
                <FormControlLabel
                  sx={{ "& span": { py: 0, px: 0.3 } }}
                  control={
                    <Checkbox
                      {...register("is_fax_delivery")}
                      sx={{ color: error ? colors.red : colors.black }}
                      onChange={() => {
                        setError(false);
                        setFaxNumberSelected(!faxNumberSelected);
                      }}
                      checked={faxNumberSelected}
                    />
                  }
                  label="Fax Number"
                  disabled={!watch("fax")}
                />
              </Box>
              {error && (
                <Typography sx={{ color: colors.red, fontSize: "12px" }}>
                  * Please select atleast one mode of delivery
                </Typography>
              )}
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigate(-1)}
              label={"Cancel"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingHealthOrg}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditHealthOrganisation;
